import React, { useMemo } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { useAuthStore } from '@/store/authStore';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import assetAuditService, { type AuditCycleDto } from '@/services/asset-audit.service';

const { Text } = Typography;

const STATUS_TAG: Record<string, string> = {
  open: 'blue',
  closed: 'default',
  cancelled: 'red',
};

const AssetAuditPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.audit.view') ||
      user?.permissions?.includes('asset.view')
  );
  const canEdit = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.audit.edit') ||
      user?.permissions?.includes('asset.edit')
  );

  const { data: cycles = [], isLoading: lc } = useQuery({
    queryKey: ['asset-audit', 'cycles'],
    queryFn: () => assetAuditService.listCycles(),
    enabled: canView,
  });

  const delCycleMut = useMutation({
    mutationFn: (id: string) => assetAuditService.removeCycle(id),
    onSuccess: () => {
      message.success('Cycle removed');
      void queryClient.invalidateQueries({ queryKey: ['asset-audit'] });
    },
    onError: (e: Error) => message.error(e?.message || 'Failed'),
  });

  const cycleCols: ColumnsType<AuditCycleDto> = useMemo(() => {
    const base: ColumnsType<AuditCycleDto> = [
      { title: 'Name', dataIndex: 'name', key: 'n', ellipsis: true },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 's',
        width: 100,
        render: (s: string) => <Tag color={STATUS_TAG[s] || 'default'}>{s}</Tag>,
      },
      {
        title: 'Period',
        key: 'p',
        width: 200,
        render: (_: unknown, r) =>
          r.period_start || r.period_end
            ? `${r.period_start ? r.period_start.slice(0, 10) : '—'} – ${
                r.period_end ? r.period_end.slice(0, 10) : '—'
              }`
            : '—',
      },
    ];
    base.push({
      title: 'Actions',
      key: 'a',
      width: 280,
      render: (_: unknown, r) => (
        <Space size="small" wrap>
          <Button type="link" size="small" onClick={() => navigate(`/asset/audit/cycles/${r.id}`)}>
            Open
          </Button>
          {canEdit && (
            <Button size="small" onClick={() => navigate(`/asset/audit/cycles/${r.id}/edit`)}>
              Edit
            </Button>
          )}
          {canEdit && (
            <Button
              danger
              size="small"
              onClick={() =>
                Modal.confirm({
                  title: 'Remove this cycle?',
                  content: `Delete “${r.name}”? This cannot be undone.`,
                  okText: 'Delete',
                  okButtonProps: { danger: true },
                  onOk: () => delCycleMut.mutateAsync(r.id),
                })
              }
            >
              Delete
            </Button>
          )}
        </Space>
      ),
    });
    return base;
  }, [canEdit, delCycleMut, navigate]);

  if (!canView) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Asset audit" documentNumber="" />}>
        <Card>Access denied. Need asset.audit.view or asset.view</Card>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Asset audit & verification"
          documentNumber=""
          primaryActions={
            <Space wrap>
              <Link to="/asset/audit/report">Audit report (summary)</Link>
              <Button onClick={() => navigate('/asset/assets')}>Back to assets</Button>
              {canEdit && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/asset/audit/cycles/new')}
                >
                  New cycle
                </Button>
              )}
            </Space>
          }
        />
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Open an audit cycle to seed or add asset lines, then record each line as found, missing, or
        damaged. Closing a cycle is optional; asset last verified date is updated when a line is not
        pending.
      </Text>
      <Table<AuditCycleDto>
        rowKey="id"
        loading={lc}
        dataSource={cycles}
        columns={cycleCols}
        pagination={{ pageSize: 10 }}
      />
    </WorkspaceLayout>
  );
};

export default AssetAuditPage;
